import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/utils";
import { CloseButton } from "@/components/CloseButton";

interface Ticket {
  id: string; ticket_id: string;
  customer_name: string; customer_email: string | null; customer_phone: string | null;
  order_number: string | null;
  subject: string; description: string;
  status: "open" | "in_progress" | "waiting_customer" | "resolved" | "closed";
  priority: "low" | "normal" | "high" | "urgent";
  category: string | null; source: string | null;
  assignee_id: string | null; created_by: string | null;
  resolved_at: string | null; resolution_summary: string | null;
  created_at: string; updated_at: string;
}
interface Comment { id: string; ticket_id: string; employee_id: string; content: string; is_internal: boolean; created_at: string; }

export function Support() {
  const { employee } = useAuth();
  const [rows, setRows] = useState<Ticket[]>([]);
  const [emps, setEmps] = useState<{ id: string; first_name: string | null; last_name: string | null; email: string }[]>([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | Ticket["status"]>("open");
  const [mineOnly, setMineOnly] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState<Ticket | null>(null);

  useEffect(() => { void load(); }, []);
  useEffect(() => {
    const ch = supabase.channel("tickets")
      .on("postgres_changes", { event: "*", schema: "public", table: "hq_support_tickets" }, () => void load())
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, []);

  async function load() {
    const [{ data: t }, { data: e }] = await Promise.all([
      supabase.from("hq_support_tickets").select("*").order("created_at", { ascending: false }).limit(500),
      supabase.from("hq_employees").select("id,first_name,last_name,email").eq("status", "active"),
    ]);
    setRows((t as Ticket[]) ?? []);
    setEmps((e as { id: string; first_name: string | null; last_name: string | null; email: string }[]) ?? []);
  }

  const empById = useMemo(() => Object.fromEntries(emps.map(e => [e.id, e])), [emps]);

  const filtered = rows.filter(r => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (mineOnly && r.assignee_id !== employee?.id) return false;
    if (q) {
      const hay = `${r.ticket_id} ${r.customer_name} ${r.customer_email ?? ""} ${r.subject} ${r.order_number ?? ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const counts = {
    open: rows.filter(r => r.status === "open").length,
    in_progress: rows.filter(r => r.status === "in_progress").length,
    waiting_customer: rows.filter(r => r.status === "waiting_customer").length,
    resolved: rows.filter(r => r.status === "resolved").length,
    mine: rows.filter(r => r.assignee_id === employee?.id && r.status !== "closed" && r.status !== "resolved").length,
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Customer Support</h1>
          <p className="text-sm text-gray2">{counts.mine} open assigned to you · {counts.open} unassigned</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn">+ New Ticket</button>
      </div>

      <div className="flex flex-wrap gap-2">
        {(["open","in_progress","waiting_customer","resolved","all"] as const).map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} className={`btn btn-sm ${statusFilter === s ? "" : "btn-ghost"}`}>
            {s.replace(/_/g, " ")} {s !== "all" && `(${(counts as Record<string,number>)[s] ?? 0})`}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-sm">
          <input type="checkbox" checked={mineOnly} onChange={e => setMineOnly(e.target.checked)} /> Mine only
        </label>
      </div>

      <input className="input" placeholder="Search by ticket, customer, order #…" value={q} onChange={e => setQ(e.target.value)} />

      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="card text-center text-sm text-gray2">No tickets match.</div>
        )}
        {filtered.map(t => {
          const a = t.assignee_id ? empById[t.assignee_id] : null;
          const display = displayCustomer(t.customer_name, t.customer_email);
          const num = t.ticket_id.replace(/^SUP-/, "#");
          return (
            <button
              key={t.id}
              onClick={() => setSelected(t)}
              className="block w-full rounded-xl bg-white p-3 text-left ring-1 ring-line transition hover:ring-teal-dark hover:shadow-soft"
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 rounded-md bg-offwhite px-2 py-1 font-mono text-[11px] font-semibold text-gray2">
                  {num}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-charcoal">{t.subject}</div>
                      <div className="mt-0.5 truncate text-xs text-gray2">
                        <span className="font-medium text-charcoal">{display.name}</span>
                        {display.suffix && <span> · {display.suffix}</span>}
                        {display.viaBadge && <span className="ml-1.5 rounded bg-offwhite px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-gray2">{display.viaBadge}</span>}
                        {t.order_number && <span> · Order {t.order_number}</span>}
                      </div>
                      <div className="mt-1 line-clamp-1 text-xs text-gray2">{t.description}</div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className={`pill ${statusPill(t.status)}`}>{t.status.replace(/_/g, " ")}</span>
                      <span className={`pill ${prioPill(t.priority)}`}>{t.priority}</span>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-gray2">
                    <span>{a ? `Assigned: ${a.first_name ?? a.email}` : "Unassigned"}</span>
                    <span>·</span>
                    <span>{fmtDate(t.created_at)}</span>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {showNew && <NewTicketModal onClose={() => setShowNew(false)} onCreated={async (t) => { setShowNew(false); await load(); setSelected(t); }} />}
      {selected && <TicketDetailModal ticket={selected} emps={emps} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
}

function NewTicketModal({ onClose, onCreated }: { onClose: () => void; onCreated: (t: Ticket) => void }) {
  const { employee } = useAuth();
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", order_number: "", subject: "", description: "", priority: "normal", category: "", source: "" });
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!employee) return;
    setBusy(true); setError(null);
    const { data, error } = await supabase.from("hq_support_tickets").insert({
      customer_name: form.customer_name,
      customer_email: form.customer_email || null,
      customer_phone: form.customer_phone || null,
      order_number: form.order_number || null,
      subject: form.subject, description: form.description,
      priority: form.priority, category: form.category || null,
      source: form.source || null,
      created_by: employee.id,
    }).select().single();
    setBusy(false);
    if (error) setError(error.message);
    else onCreated(data as Ticket);
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
        <h2 className="mb-4 text-xl font-bold">New Support Ticket</h2>
        {error && <div className="mb-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
          <div><label className="label">Customer name *</label><input required className="input" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} autoFocus /></div>
          <div><label className="label">Order #</label><input className="input" value={form.order_number} onChange={e => setForm({ ...form, order_number: e.target.value })} /></div>
          <div><label className="label">Email</label><input type="email" className="input" value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} /></div>
          <div><label className="label">Phone</label><input className="input" value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className="label">Subject *</label><input required className="input" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className="label">Description *</label><textarea required rows={4} className="input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div><label className="label">Priority</label><select className="input" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>{["low","normal","high","urgent"].map(p => <option key={p}>{p}</option>)}</select></div>
          <div><label className="label">Source</label><select className="input" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}><option value="">—</option>{["email","phone","chat","social","walk_in","other"].map(s => <option key={s}>{s}</option>)}</select></div>
          <div><label className="label">Category</label><input className="input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="shipping, refund, product…" /></div>
          <div className="sm:col-span-2 flex justify-end gap-2"><button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn" disabled={busy}>{busy ? "Creating…" : "Create"}</button></div>
        </form>
      </div>
    </div>
  );
}

function TicketDetailModal({ ticket, emps, onClose, onChanged }: { ticket: Ticket; emps: { id: string; first_name: string | null; last_name: string | null; email: string }[]; onClose: () => void; onChanged: () => Promise<void> }) {
  const { employee } = useAuth();
  const [t, setT] = useState<Ticket>(ticket);
  const [comments, setComments] = useState<Comment[]>([]);
  const [draft, setDraft] = useState("");
  const [replyMode, setReplyMode] = useState<"internal" | "customer">("internal");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const empById = Object.fromEntries(emps.map(e => [e.id, e]));

  useEffect(() => {
    void supabase.from("hq_support_ticket_comments").select("*").eq("ticket_id", ticket.id).order("created_at").then(({ data }) => setComments((data as Comment[]) ?? []));
    const ch = supabase.channel(`ticket-${ticket.id}`).on("postgres_changes", { event: "INSERT", schema: "public", table: "hq_support_ticket_comments", filter: `ticket_id=eq.${ticket.id}` }, payload => {
      const c = payload.new as Comment;
      setComments(prev => prev.some(x => x.id === c.id) ? prev : [...prev, c]);
    }).subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [ticket.id]);

  async function update(patch: Partial<Ticket>) {
    setT({ ...t, ...patch });
    const final = { ...patch };
    if (patch.status === "resolved") final.resolved_at = new Date().toISOString();
    await supabase.from("hq_support_tickets").update(final).eq("id", t.id);
    await onChanged();
  }
  async function send() {
    if (!draft.trim() || !employee || sending) return;
    const content = draft.trim();
    setSendError(null);
    if (replyMode === "internal") {
      setDraft("");
      await supabase.from("hq_support_ticket_comments").insert({ ticket_id: t.id, employee_id: employee.id, content, is_internal: true });
      return;
    }
    if (!t.customer_email) { setSendError("This ticket has no customer email — switch to Internal."); return; }
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Not signed in.");
      const res = await fetch("/.netlify/functions/ticket-reply", {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ticket_id: t.id, message: content }),
      });
      if (!res.ok) {
        const tx = await res.text();
        let msg = tx; try { msg = JSON.parse(tx).error || tx; } catch {}
        throw new Error(msg);
      }
      setDraft("");
      // The function inserts a public comment & may bump status to waiting_customer — refresh.
      await onChanged();
      void supabase.from("hq_support_ticket_comments").select("*").eq("ticket_id", t.id).order("created_at").then(({ data }) => setComments((data as Comment[]) ?? []));
    } catch (e) {
      setSendError((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-4xl overflow-auto rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="font-mono text-xs text-gray2">{t.ticket_id}</div>
            <h2 className="text-xl font-bold">{t.subject}</h2>
            <div className="mt-1 text-sm text-gray2">{t.customer_name}{t.customer_email ? ` · ${t.customer_email}` : ""}{t.customer_phone ? ` · ${t.customer_phone}` : ""}</div>
            {t.order_number && <div className="text-xs text-gray2">Order: <strong>{t.order_number}</strong></div>}
          </div>
          <CloseButton onClick={onClose} className="text-gray2 hover:text-ink" />
        </div>

        <div className="grid gap-5 sm:grid-cols-[1fr_220px]">
          <div className="space-y-4">
            <div className="rounded-lg bg-offwhite p-3 text-sm whitespace-pre-wrap">{t.description}</div>
            <div>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray2">Conversation</h3>
              <div className="space-y-2">
                {comments.map(c => {
                  const e = empById[c.employee_id];
                  return (
                    <div key={c.id} className={`rounded-lg p-3 text-sm ${c.is_internal ? "bg-offwhite" : "bg-teal-mint/40 border-l-4 border-teal-dark"}`}>
                      <div className="text-xs text-gray2">
                        {e?.first_name ?? "Someone"} · {fmtDate(c.created_at)}
                        {!c.is_internal && <span className="ml-2 rounded bg-teal-dark px-1.5 py-0.5 text-[10px] font-semibold text-white">sent to customer</span>}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap">{c.content}</div>
                    </div>
                  );
                })}
                <div className="space-y-2 rounded-lg border border-line p-3">
                  <div className="flex gap-2 text-xs">
                    <button onClick={() => setReplyMode("internal")} className={`rounded px-2 py-1 font-semibold ${replyMode === "internal" ? "bg-charcoal text-white" : "bg-offwhite text-gray2"}`}>Internal note</button>
                    <button onClick={() => setReplyMode("customer")} disabled={!t.customer_email} title={t.customer_email ? "" : "Ticket has no customer email"} className={`rounded px-2 py-1 font-semibold ${replyMode === "customer" ? "bg-teal-dark text-white" : "bg-offwhite text-gray2"} disabled:opacity-50`}>Reply to customer</button>
                  </div>
                  {sendError && <div className="rounded bg-bad-bg px-2 py-1 text-xs text-bad">{sendError}</div>}
                  <textarea
                    className="input min-h-[80px]"
                    placeholder={replyMode === "internal" ? "Internal note — not visible to the customer" : `Email to ${t.customer_email ?? "customer"}`}
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                  />
                  <div className="flex justify-end">
                    <button onClick={send} disabled={sending || !draft.trim()} className="btn">
                      {sending ? "Sending…" : replyMode === "internal" ? "Add note" : "Send email"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div><label className="label">Status</label><select className="input" value={t.status} onChange={e => update({ status: e.target.value as Ticket["status"] })}>{["open","in_progress","waiting_customer","resolved","closed"].map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select></div>
            <div><label className="label">Priority</label><select className="input" value={t.priority} onChange={e => update({ priority: e.target.value as Ticket["priority"] })}>{["low","normal","high","urgent"].map(p => <option key={p}>{p}</option>)}</select></div>
            <div><label className="label">Assignee</label><select className="input" value={t.assignee_id ?? ""} onChange={e => update({ assignee_id: e.target.value || null })}><option value="">—</option>{emps.map(e => <option key={e.id} value={e.id}>{e.first_name ?? e.email}</option>)}</select></div>
            <div><label className="label">Category</label><input className="input" value={t.category ?? ""} onChange={e => update({ category: e.target.value || null })} /></div>
            {t.status === "resolved" && (
              <div><label className="label">Resolution summary</label><textarea className="input" rows={3} value={t.resolution_summary ?? ""} onChange={e => update({ resolution_summary: e.target.value || null })} /></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Clean up the displayed customer for tickets that came in as forwarded automation
// (Shopify notifier sends from b3.RANDOM_HASH@mailer3.shopify.com which is unreadable).
function displayCustomer(name: string | null, email: string | null): { name: string; suffix: string | null; viaBadge: string | null } {
  const e = (email || "").trim();
  const lower = e.toLowerCase();
  // Shopify auto-notifier (mailer3.shopify.com, replies@mail.shopify.com, etc.)
  if (/@mailer\d*\.shopify\.com$/.test(lower) || /@(mail|replies)\.shopify\.com$/.test(lower)) {
    return { name: "Shopify notification", suffix: null, viaBadge: "via Shopify" };
  }
  // Klaviyo, SendGrid, intercom, etc — generic automation prefixes
  if (/^(noreply|no-reply|notifications?|alerts?|donotreply)@/.test(lower)) {
    return { name: "Automated message", suffix: lower.split("@")[1] ?? null, viaBadge: null };
  }
  // If name and email are identical → just show email
  const n = (name || "").trim();
  if (!n || n === e) {
    if (!e) return { name: "Unknown", suffix: null, viaBadge: null };
    const [u, d] = e.split("@");
    // Hide super-long random-looking local parts (e.g. b3.BAABAQg...)
    if (u && u.length > 24) return { name: d || e, suffix: null, viaBadge: null };
    return { name: e, suffix: null, viaBadge: null };
  }
  return { name: n, suffix: e || null, viaBadge: null };
}

function statusPill(s: Ticket["status"]): string {
  if (s === "resolved" || s === "closed") return "pill-ok";
  if (s === "in_progress") return "pill-mint";
  if (s === "waiting_customer") return "pill-warn";
  return "pill-warn";
}
function prioPill(p: Ticket["priority"]): string {
  if (p === "urgent") return "pill-bad";
  if (p === "high") return "pill-warn";
  if (p === "low") return "";
  return "pill-mint";
}
